/**
 * The movement controller. Pillar 1.
 *
 * Runs at a fixed 120 Hz substep, independent of both render rate and the
 * 60 Hz cell simulation. See docs/02-movement.md §1 for why.
 *
 * The controller's job is to turn intent into velocity; `body.ts` turns
 * velocity into position. Keeping those separate is what lets traversal
 * abilities compose — an ability changes intent, and collision behaviour stays
 * in exactly one place.
 *
 * Two rules that are easy to violate and expensive to get wrong:
 *   - **Never lock input on landing.** Not for one frame.
 *   - **Attacks apply drag, never a velocity stop.** Enforced in combat, but
 *     the controller must not fight it either.
 */

import { approach, clamp, clamp01, lerp } from '../core/math';
import type { Input } from '../core/input';
import { Category, Flag, M, OneWay, Tag, VOID } from '../sim/materials';
import type { CellWorld } from '../sim/world';
import type { Particles } from '../sim/particles';
import { BodyFlag, type World } from './ecs';
import { ONE_WAY_IGNORE, groundMaterialAt, solidIn, stepBody, type BodyResult, type StepLimits } from './body';
import {
  applyModifiers, fluidSpeedScale, jumpVelocity, type MovementModifiers, type MovementTuning,
} from './movementTuning';
// The crouch ratio lives with the drawn body rather than here, and the arrow
// points this way on purpose: `pixelBody.ts` is a leaf (it imports `core/rng`
// and `sim/materials` and nothing else), and it is the *constrained* side of
// the crouch — a collider can halve freely, a silhouette can only fold as far
// as its own bone thicknesses allow. Owning the number where the constraint is
// keeps the two halves from being two numbers. See `CROUCH_HEIGHT`.
import { CROUCH_HEIGHT } from './pixelBody';

/**
 * Stick deflection past which the vertical counts as "the player means down".
 *
 * The same 0.5 `core/input.ts`'s `aimAngleFor` quantises the aim at and the
 * dash's 8-way quantiser below uses, and it is shared on purpose: a player who
 * has learned where a swing starts pointing down has learned where a dash does
 * and where a drop does.
 *
 * It reads the **axis**, never `held('down')`, for the reason `wielder.ts` gives
 * at its own aim read: the axis is where the keyboard and the pad have already
 * been reconciled. The stronger form of that argument is measured in
 * `tests/downOwnership.test.ts` — the left stick never sets `held('down')` at
 * all, so a verb written against the action is a verb a stick cannot reach.
 */
export const DOWN_INTENT = 0.5;

/**
 * How far below the feet a drop looks for the bottom of the one-way run, and
 * the hard bound on how far one ticket may carry the body.
 *
 * Sixteen, against a measurement rather than a feel: sampled over the real
 * generator (eight seeds, both modes, four bands, 5.99 M cells) the 99 474
 * vertical one-way runs are 73.2% exactly 3 cells deep, 21.7% one cell, and
 * the **deepest run in the whole sample is 7**. Sixteen is more than twice
 * that, so the geometric exit is what ends every real drop and this number is
 * only ever the backstop — but it is a *bound*, so a run deeper than anything
 * the generator makes (or a deck the player has just built) degrades into "the
 * body stopped early" rather than into noclip.
 */
export const DROP_SCAN_MAX = 16;

/**
 * Seconds a drop ticket may live if the body never descends at all.
 *
 * Second-order: `DROP_SCAN_MAX` already bounds the ticket in *space*, and free
 * fall covers 16 cells in 0.146 s at `fallGravity`. This catches only the case
 * where something is holding the body up — an updraft, a tether, deep mercury —
 * so it does not keep the ticket forever.
 */
export const DROP_TIMEOUT = 0.35;

/**
 * Is the player asking to go down, from the one value both input paths write?
 *
 * Exported so `tests/downOwnership.test.ts` can assert the rule rather than
 * transcribe the threshold: a test that re-derives `> 0.5` is testing its own
 * arithmetic.
 */
export function wantsDrop(axisY: number): boolean {
  return axisY > DOWN_INTENT;
}

/**
 * Is the player asking to crouch (#98)?
 *
 * The same question as `wantsDrop` and deliberately a second function rather
 * than a second caller of the first. `down` is a *modifier* — the rule #87
 * settled is that nothing takes it and arbitration happens at the other
 * control — so crouch and drop both read it and neither consumes it: down alone
 * crouches, down plus `jump` on a semi-solid drops, and the drop's own refusal
 * path decides which. Naming both predicates keeps that readable at the call
 * sites and lets `tests/crouch.test.ts` assert the two agree over every
 * deflection instead of transcribing `0.5` a third time.
 */
export function wantsCrouch(axisY: number): boolean {
  return axisY > DOWN_INTENT;
}

/**
 * Ground speed while crouched, as a fraction of standing.
 *
 * A crouch that costs nothing is a crouch you hold permanently, and then the
 * smaller hitbox is a free defensive buff rather than a trade — #87 said
 * "probably slows or stops horizontal movement" and the reason it is slow
 * rather than stopped is the measurement in #98: 52% of the crouch-only runs
 * the generator makes are three or more cells wide and adjoin floor you can
 * stand on, so a crouch you cannot travel in cannot reach the far side of the
 * shape the verb exists for. 0.45 leaves a 106-cell run — the longest in the
 * sample — about four seconds of crawl.
 *
 * Applied as a multiplier on the *target* speed and the acceleration together,
 * exactly as `attackDrag` is, so it is a drag and never a stop (docs/02 §4).
 */
export const CROUCH_SPEED_SCALE = 0.45;

/**
 * Submersion fraction (`World.submersion`, 0..1) above which a body is
 * swimming rather than merely wading — the same threshold `stepMover`'s own
 * `deepWater` reads to pick `MoveState.SWIM`. Exported (#169B) so
 * `PixelBodySystem.poseOne` selects `Pose.SWIM` at exactly the depth the
 * physics already calls swimming, rather than a second number that could
 * drift from this one.
 */
export const DEEP_WATER_SUBMERSION = 0.6;

/**
 * How long `MoverState.tailAnchored` stays set after a tail-reach launch —
 * issue #186. Long enough for a HUD or a squash to read it (`m.squashTime`'s
 * own 0.09s is the shortest feedback window this controller trusts, per
 * `handleJump`'s ordinary jump), short enough that it is gone well before a
 * second reach could fire, so it never reads as "always anchored".
 */
export const TAIL_ANCHOR_VISUAL_TIME = 0.15;

export const Ability = {
  AIRSTEP: 1 << 0,
  CLING: 1 << 1,
  TETHER: 1 << 2,
  BREACH: 1 << 3,
  BLINK: 1 << 4,
  BURROW: 1 << 5,
  FLUID_ADAPT: 1 << 6,
  PHASE_DRIFT: 1 << 7,
  UPDRAFT: 1 << 8,
} as const;
export type AbilityMask = number;

export const MoveState = {
  GROUND: 0,
  AIR: 1,
  WALL_SLIDE: 2,
  CLING: 3,
  DASH: 4,
  BLINK: 5,
  BURROW: 6,
  PHASED: 7,
  SWIM: 8,
  TETHER: 9,
  /** Sustained fuel-metered lift — issue #171. `verticalMode: 'hover-thrust'`. */
  HOVER: 10,
  /** Full free vertical control on a fuel meter — issue #171. `verticalMode: 'flight'`. */
  FLIGHT: 11,
} as const;
export type MoveStateId = (typeof MoveState)[keyof typeof MoveState];

/**
 * Per-entity movement state. One instance for the player; enemies with
 * platforming AI can own one too.
 */
export interface MoverState {
  entity: number;
  tuning: MovementTuning;
  abilities: AbilityMask;
  state: MoveStateId;

  // timers, all in seconds and all counting down
  coyote: number;
  wallCoyote: number;
  wallCoyoteDir: number;
  dashTime: number;
  dashCooldown: number;
  dashPostGravity: number;
  iframes: number;
  inputLock: number;
  wallGrace: number;
  blinkCooldown: number;
  phaseTime: number;
  phaseCooldown: number;

  /**
   * Recent (x, y) samples, oldest first, for `dashMode: 'rewind'` — issue
   * #177. Only ever populated by `stepMover` when the mover's own tuning
   * selects rewind, so every `'translate'` dash (still nearly all of them)
   * pays nothing for this — no allocation, no per-substep aging loop.
   */
  rewindHistory: { x: number; y: number; age: number }[];

  dashesLeft: number;
  airDashesLeft: number;
  /** Substeps elapsed since the current dash started; i-frames arm on 1. */
  dashSubsteps: number;
  lastWallDir: number;
  /** Previous substep's wall contact, so grace arms on contact only. */
  prevOnWall: number;
  jumpHeld: boolean;
  /**
   * A rise that an early release may still cut. Armed only if jump was
   * actually held at launch — see `handleJump`.
   */
  jumpCutArmed: boolean;
  facing: number;

  // presentation hooks, read by the renderer
  squashX: number;
  squashY: number;
  squashTime: number;
  landImpact: number;
  dashDirX: number;
  dashDirY: number;

  /**
   * The rope: where it is tied, how long it currently is, and whether it exists.
   *
   * Written and stepped by `abilities.ts` — radial clamp, tangent projection,
   * severing on terrain and on anchor destruction. For a long time that was the
   * *only* code that touched them: nothing in the renderer read the anchor, so
   * the physics was correct and completely invisible, and the only tether-ish
   * thing on screen was the whip's forward swing crack while the anchor was
   * overhead. That right angle is what #116 was reported as.
   *
   * `abilities.tetherRope` and `abilities.tetherDrawAngle` are the presentation
   * readers, and `tests/tetherRope.test.ts` holds them to the anchor within a
   * pixel. Anything new that wants to show the tether goes through those two
   * rather than re-deriving the geometry, because a second derivation is how the
   * drawn angle and the cutting angle came apart in #112.
   */
  tetherActive: boolean;
  tetherX: number;
  tetherY: number;
  tetherLen: number;

  // --- traversal ability state (see entity/abilities.ts) ---
  /**
   * Which ability the one context-selected button would fire *right now*,
   * recomputed every substep by `updateAbilities` — not the last one used. The
   * HUD previews it, and docs/02 §6.5's premise ("the player learns this button
   * does the right thing here") is not learnable if pressing is the only way to
   * find out what is armed.
   */
  chosen: number;
  breachTime: number;
  breachCooldown: number;
  breachDirX: number;
  breachDirY: number;
  burrowing: boolean;
  burrowPayload: number;
  burrowPayloadMat: number;
  jetCooldown: number;
  blinkCharges: number;
  blinkRegen: number;
  /** Origin left behind by the last blink, and how long it stays returnable. */
  snapbackX: number;
  snapbackY: number;
  snapbackTime: number;

  /**
   * A breach is in progress. Not `breachTime > 0`: the timer is floored at
   * zero, so the substep on which it expires never re-enters the sustained
   * branch. A charge that simply ran its course would leave IGNORE_TERRAIN set
   * and gravity at zero — permanent noclip.
   */
  breaching: boolean;
  breachOriginX: number;
  breachOriginY: number;
  /**
   * The gravity scale an ability suspended, and whether it is currently held.
   * Restoring a hard `1` on exit would clobber whatever else owned it — a hover
   * attack sets it for the duration of a swing (`combat/wielder.ts`).
   */
  savedGravity: number;
  gravityHeld: boolean;
  /** Updraft is a sustained column (docs/02 §3 row 9), not an impulse. */
  updraftTime: number;
  updraftPower: number;
  updraftTick: number;
  phaseBudget: number;
  phaseOriginX: number;
  phaseOriginY: number;

  // --- drop-through (issue #87) ---
  /**
   * A drop ticket is in flight. Not `dropTime > 0`: the timer is only the
   * backstop and the ticket normally ends on the geometric test below, so a
   * spent ticket routinely has time left on the clock. Reading the timer as
   * the state is the noclip-that-never-ends bug `breaching` is commented
   * against, in the one other place this file keeps a flag beside a timer.
   */
  dropping: boolean;
  /**
   * World y the **feet** must pass for the ticket to be spent: one row below
   * the bottom of the contiguous one-way run the drop started in. The scan
   * that finds it is bounded to `DROP_SCAN_MAX`, so this is too.
   *
   * A *height*, not a countdown. Sized in frames instead, a ticket tuned on a
   * one-cell plank strands the body inside the 3-cell decks that are 73% of
   * the ones the generator lays.
   */
  dropExitY: number;
  /** Backstop only — see `DROP_TIMEOUT`. */
  dropTime: number;

  // --- crouch (issue #98) ---
  /**
   * The body is crouched: `halfH` is lowered and `w.crouchDrop` holds by how
   * much.
   *
   * Kept here as well as in the ECS for the reason `dropping` and `breaching`
   * are: the column is the *amount*, and reading an amount as a state is how a
   * float that never quite reaches zero becomes a permanent stance. The two are
   * asserted equal by `tests/crouch.test.ts`.
   */
  crouching: boolean;
  /**
   * The crouch is being held down by a ceiling rather than by the player.
   *
   * Not decoration and not only for a HUD hint. It is the difference between
   * the two failures a crouch can ship with — clipping into rock and being
   * unable to move — and a controller that has neither must be able to say
   * which of the two it is avoiding. #98 measured the shape it happens in:
   * 9.31% of floor cells admit a crouch and not a stand, and 52% of the wide
   * runs of them adjoin floor you *can* stand on, so this is set and cleared
   * routinely rather than at the edges.
   */
  standBlocked: boolean;

  // --- flight / hover-thrust (issue #171) ---
  /**
   * Seconds of lift left in `verticalMode: 'flight'`/`'hover-thrust'`. Reset
   * to `flightCapacity(t)` on landing and on wall contact, the same
   * "forgiveness window" spot `dashesLeft`/`airDashesLeft` already reset in
   * — grounding a fuel-metered body is the refill condition, same as
   * grounding refills every other per-airborne-trip resource here.
   */
  flightFuel: number;
  /**
   * The fuel ran out mid-air rather than on landing — `'hover-thrust'`
   * overheating, per the archetype cards. Purely a HUD/telemetry flag today:
   * `m.flightFuel <= 0` already gates re-entering `HOVER`/`FLIGHT` on its
   * own, so this adds no behaviour, only "why is the meter empty" for a
   * future UI to read.
   */
  overheated: boolean;

  // --- stances (issue #185) ---
  /**
   * The archetype-authored mode this body is currently in — Flesh-machine
   * hybrid's `'organic'`/`'machine'`, `null` for every archetype whose
   * `movement.stances` is empty. Read the way `flightFuel` is: a per-mover
   * mode flag with no reader outside this module and whoever forces a swap
   * (`Game.tryRevive`'s `restance` case).
   */
  stanceId: string | null;
  /**
   * `tuning` before any stance delta is stacked on it — the archetype's own
   * resolved package, i.e. what `createMover` was handed. `setStance`
   * recomputes `tuning` from *this* every time rather than from the
   * previous `tuning`, so swapping organic → machine → organic returns to
   * exactly the midpoint's numbers instead of compounding a delta onto a
   * delta.
   */
  baseTuning: MovementTuning;

  // --- tail anchor (issue #186) ---
  /**
   * The grounded jump just fired as an aimed tail-reach rather than a
   * vertical launch — `t.tailAnchorReach`'s one reader outside `handleJump`.
   * Telemetry/HUD only today, the same role `overheated` has: `handleJump`
   * does not consult this to decide anything, it only sets it. Kept as a
   * flag alongside `tailAnchorTime` rather than reading the timer as the
   * state, for the reason `dropping`/`breaching` give theirs.
   */
  tailAnchored: boolean;
  /** Counts down to 0; `tickTimers` clears `tailAnchored` when it gets there. */
  tailAnchorTime: number;
}

export function createMover(entity: number, tuning: MovementTuning, abilities = 0): MoverState {
  return {
    entity,
    tuning,
    abilities,
    state: MoveState.AIR,
    coyote: 0,
    wallCoyote: 0,
    wallCoyoteDir: 0,
    dashTime: 0,
    dashCooldown: 0,
    dashPostGravity: 0,
    iframes: 0,
    inputLock: 0,
    wallGrace: 0,
    blinkCooldown: 0,
    phaseTime: 0,
    phaseCooldown: 0,
    rewindHistory: [],
    dashesLeft: 1,
    airDashesLeft: 0,
    dashSubsteps: 0,
    lastWallDir: 0,
    prevOnWall: 0,
    jumpHeld: false,
    jumpCutArmed: false,
    facing: 1,
    squashX: 1,
    squashY: 1,
    squashTime: 0,
    landImpact: 0,
    dashDirX: 1,
    dashDirY: 0,
    tetherActive: false,
    tetherX: 0,
    tetherY: 0,
    tetherLen: 0,
    chosen: 0,
    breachTime: 0,
    breachCooldown: 0,
    breachDirX: 1,
    breachDirY: 0,
    burrowing: false,
    burrowPayload: 0,
    burrowPayloadMat: 0,
    jetCooldown: 0,
    blinkCharges: 2,
    blinkRegen: 0,
    snapbackX: 0,
    snapbackY: 0,
    snapbackTime: 0,
    breaching: false,
    breachOriginX: 0,
    breachOriginY: 0,
    savedGravity: 1,
    gravityHeld: false,
    updraftTime: 0,
    updraftPower: 1,
    updraftTick: 0,
    phaseBudget: 0,
    phaseOriginX: 0,
    phaseOriginY: 0,
    dropping: false,
    dropExitY: 0,
    dropTime: 0,
    crouching: false,
    standBlocked: false,
    flightFuel: flightCapacity(tuning),
    overheated: false,
    stanceId: null,
    baseTuning: tuning,
    tailAnchored: false,
    tailAnchorTime: 0,
  };
}

/**
 * Force this mover into a named stance — issue #185. `modifiers` is the
 * stance's own delta (`meta/archetypes.ts`'s `resolveStance`, kept structural
 * here for the same reason `resolveMovement`'s result already is: this module
 * stays ignorant of `meta/`), stacked on `m.baseTuning` rather than on the
 * current `m.tuning` so repeated swaps never compound.
 *
 * The one caller today is `Game.tryRevive`'s `restance` case — a lethal hit
 * forcing the *other* stance. A live player-triggered swap (binding a button
 * to call this directly) is deliberately not wired: it needs input-binding
 * plumbing this issue did not scope, and the revival path is the acceptance
 * criterion this function exists to satisfy.
 */
export function setStance(m: MoverState, stanceId: string, modifiers: Readonly<MovementModifiers>): void {
  m.stanceId = stanceId;
  m.tuning = applyModifiers(m.baseTuning, modifiers);
}

/**
 * Full fuel for whichever `verticalMode` this body carries — issue #171.
 * `'flight'` spends `flightSeconds`; everything else has no meter, so 0.
 *
 * `'hover-thrust'` prefers `thermalSeconds` (Techno-knight, 2.2 — the body
 * with the water-vents/water-shorts tension) but falls back to
 * `flightSeconds` when there is no thermal model (Modular drone swarm:
 * `verticalMode: 'hover-thrust'`, `flightSeconds: 1.8`, `thermalSeconds`
 * left at 0 — its own card carries no overheat or water tension at all).
 * A hover-thrust body always needs *some* capacity to have a meter; which
 * field authors it is the difference between "has a thermal model" and
 * "just burns fuel like flight does."
 */
export function flightCapacity(t: MovementTuning): number {
  if (t.verticalMode === 'flight') return t.flightSeconds;
  if (t.verticalMode === 'hover-thrust') return t.thermalSeconds > 0 ? t.thermalSeconds : t.flightSeconds;
  return 0;
}

/**
 * `HOVER`'s effective gravity, as a fraction of ordinary fall gravity —
 * issue #171. Low enough to hold altitude against a light tap and gain some
 * with a held press, high enough that hover-thrust reads as *resisting*
 * gravity rather than replacing it the way `FLIGHT`'s full cancel does —
 * the two `verticalMode`s are meant to feel different, not like the same
 * ability with a different name.
 */
export const HOVER_GRAVITY_SCALE = 0.15;

/** Spends `dt` of flight/hover-thrust fuel, and marks the meter as having run dry mid-air. */
function drainFlightFuel(m: MoverState, dt: number): void {
  m.flightFuel = Math.max(0, m.flightFuel - dt);
  if (m.flightFuel <= 0) m.overheated = true;
}

/** Scratch, mutated per substep — `stepBody` reads it and does not retain it. */
const stepLimits: StepLimits = { stepUp: 3, cornerCorrection: 4, minGapCells: 10 };

/**
 * What combat is doing to the body this substep.
 *
 * The caller fills it in from `combat/wielder.ts` (`isAttacking`,
 * `attackDragOf`, `isStaggered`) and the controller reads it, so the dependency
 * runs one way only: docs/04 §8's "two heavy weapons reduce mobility
 * measurably" is a *movement* consequence of a *combat* fact, and movement
 * importing combat to learn it would close a cycle through the one module that
 * has to stay independently testable.
 *
 * One retained object, mutated in place by the caller — the substep runs 120
 * times a second and must not allocate.
 */
export interface CombatMoveState {
  /** A swing is committing the character. */
  attacking: boolean;
  /**
   * Speed and acceleration multiplier **this substep** — read whether or not a
   * swing is in flight. Two terms are folded in (docs/04 §8, issue #30): a
   * per-swing cost (×0.6 heaviest, ×0.9 lightest) and a standing weight cost
   * for what the pair *is*, so two hammers are slower than hammer-and-dagger
   * even between swings. Attacks drag, never stop (docs/02 §4).
   */
  drag: number;
  /** The swing has confirmed, so a jump may cancel it (docs/02 §4). */
  jumpCancel: boolean;
  /** Poise broken: input authority is suspended until it expires. */
  staggered: boolean;
}

export interface MoveContext {
  cells: CellWorld;
  particles: Particles | null;
  /** Camera shake, dust, hitstop — presentation reacting to movement events. */
  onLand?: (impact: number, x: number, y: number, mat: number) => void;
  onDash?: (dirX: number, dirY: number) => void;
  onWallJump?: (dir: number) => void;
  onFootstep?: (x: number, y: number, mat: number) => void;
  /** Absent for movers with no weapons — AI platformers, mostly. */
  combat?: CombatMoveState;
}

/**
 * Advance one mover by exactly one fixed substep.
 * `input` may be null for AI-driven movers; they write intent directly.
 */
export function stepMover(
  m: MoverState,
  w: World,
  input: Input | null,
  dt: number,
  ctx: MoveContext,
): void {
  const i = m.entity;
  const t = m.tuning;
  const cells = ctx.cells;

  // Recorded before anything else this substep moves the body, so a rewind
  // triggered later this same substep still reads last substep's resting
  // spot rather than the one about to be computed — issue #177.
  if (t.dashMode === 'rewind') {
    for (const h of m.rewindHistory) h.age += dt;
    m.rewindHistory.push({ x: w.x[i], y: w.y[i], age: 0 });
    while (m.rewindHistory.length > 0 && m.rewindHistory[0].age > t.dashRewindSeconds) {
      m.rewindHistory.shift();
    }
  }

  tickTimers(m, dt);
  // Before anything reads `grounded`: while a ticket is live the body is not
  // standing on the platform it is leaving, and `stepBody` has already said so.
  updateDropThrough(m, w, i);

  // --- intent ---
  let ix = 0;
  if (input) {
    ix = Math.abs(input.axis.x) > 0.15 ? input.axis.x : 0;
  }
  // Input lock decays rather than switching off, so a wall jump blends back to
  // full authority instead of the game snatching the controller away.
  if (m.inputLock > 0) {
    const authority = 1 - clamp01(m.inputLock / t.wallJumpInputLock);
    ix *= authority;
  }

  // A broken poise bar is the one thing that takes the stick away, and the only
  // exception to the controller's "never lock input" rule — it is a state an
  // attacker had to earn, it is visible, and it ends on a timer the ECS owns.
  // Until this read, poise damage had no movement consequence at all: staggered
  // and standing were the same character.
  const staggered = ctx.combat?.staggered === true;
  if (staggered) ix = 0;

  const grounded = w.grounded[i] === 1;
  const onWall = w.onWall[i];
  const submersion = w.submersion[i];
  const deepWater = submersion > DEEP_WATER_SUBMERSION;

  // Before the state selection and the horizontal, so this substep's speed and
  // this substep's collider are the same body's. Resizing after `applyHorizontal`
  // would accelerate a standing body and then integrate a crouched one, which
  // is a one-substep discrepancy that only shows up as a stutter on the frame
  // you duck.
  updateCrouch(m, w, i, input, cells, grounded, staggered);

  if (ix !== 0) m.facing = ix > 0 ? 1 : -1;
  w.facing[i] = m.facing;

  // --- forgiveness windows ---
  if (grounded) {
    m.coyote = t.coyoteTime;
    m.dashesLeft = 1;
    m.airDashesLeft = airDashBudget(m);
    m.lastWallDir = 0;
    // Issue #171: landing is the refill condition for flight/hover-thrust
    // fuel, the same "grounding resets the airborne budget" rule every
    // other per-trip resource above already follows.
    m.flightFuel = flightCapacity(t);
    m.overheated = false;
  }
  if (onWall !== 0) {
    m.wallCoyote = t.wallCoyote;
    m.wallCoyoteDir = onWall;
    m.dashesLeft = 1;
  }
  // Issue #171. Water is both the vent and the short (Techno-knight,
  // docs/03's own note on the archetype) — a body with a thermal model
  // (`thermalSeconds > 0`) cools off while wet, refilling the meter the
  // same instant way landing does, but the same water shorts out active
  // thrust: the state-selection thrust check below is gated on staying
  // dry. `thermalSeconds === 0` — Modular drone swarm's flight-fuelled
  // hover-thrust, and every `'flight'` body — has no thermal model at all,
  // so water does nothing special to them; only `'hover-thrust'` bodies
  // that actually overheat get this tension.
  const shortedByWater = t.thermalSeconds > 0 && submersion > 0.02;
  if (shortedByWater) {
    m.flightFuel = flightCapacity(t);
    m.overheated = false;
  }

  // --- state selection ---
  if (m.dashTime > 0) {
    m.state = MoveState.DASH;
  } else if (m.phaseTime > 0) {
    m.state = MoveState.PHASED;
  } else if (deepWater) {
    m.state = MoveState.SWIM;
  } else if (grounded) {
    m.state = MoveState.GROUND;
  } else if (onWall !== 0 && wallMaterial(cells, w, i, onWall) !== VOID) {
    // Cling is a *held* grip, not a state the player falls into. Involuntary
    // cling made the entire wall-slide branch — and with it the per-material
    // slide table docs/02 §2.5 calls a first-class example of the material
    // system feeding movement — unreachable for the rest of the run the moment
    // the ability was picked up. Ungripped, a Cling owner wall-slides like
    // anyone else; the material gate applies to the grip, not to the wall.
    m.state = wantsGrip(m, input) && canCling(m, cells, w, i, onWall)
      ? MoveState.CLING
      : MoveState.WALL_SLIDE;
  } else if (
    // Issue #171. Holding jump in the air, with fuel and dry, is what
    // `'flight'`/`'hover-thrust'` bodies do instead of free-falling.
    // Gated on `!staggered` for the same reason `handleJump`'s own press
    // is skipped while staggered a few lines below — a broken poise bar
    // takes the stick away from every other airborne verb here too.
    input !== null && !staggered && input.held('jump') && !shortedByWater
    && (t.verticalMode === 'flight' || t.verticalMode === 'hover-thrust')
    && m.flightFuel > 0
  ) {
    m.state = t.verticalMode === 'flight' ? MoveState.FLIGHT : MoveState.HOVER;
  } else {
    m.state = MoveState.AIR;
  }

  // --- horizontal ---
  if (m.state !== MoveState.DASH) {
    // The crouch folds into the same drag term the loadout uses, rather than
    // clamping the speed afterwards: a clamp is a stop wearing a multiplier's
    // clothes, and docs/02 §4's rule is that nothing in this controller stops
    // the body. A product also composes — crouching with two hammers is slower
    // than either, which is the answer a player would predict.
    const crouchDrag = m.crouching ? CROUCH_SPEED_SCALE : 1;
    applyHorizontal(m, w, i, ix, grounded, submersion, dt, attackDrag(ctx) * crouchDrag);
  }

  // --- vertical ---
  applyVertical(m, w, i, input, grounded, onWall, submersion, dt, ctx);

  // --- actions ---
  // Stagger suppresses the *verbs*, not the buffers: a jump pressed during the
  // stagger is still buffered and fires the substep it ends (docs/02 §4).
  if (input && !staggered) {
    handleDash(m, w, i, input, ix, ctx);
    handleJump(m, w, i, input, ix, grounded, onWall, submersion, ctx);
  }

  // Dash overrides velocity entirely for its duration. State is re-asserted
  // here rather than only in the selection block above, so a dash started this
  // substep is already in DASH state when anything reads it — otherwise the
  // first frame of every dash reports the previous state, which desynchronises
  // animation and i-frame logic from the actual movement.
  if (m.dashTime > 0) {
    m.state = MoveState.DASH;
    w.vx[i] = m.dashDirX * t.dashSpeed;
    w.vy[i] = m.dashDirY * t.dashSpeed;
  }

  // --- integrate ---
  const phased = m.phaseTime > 0;
  if (phased) w.bodyFlags[i] |= BodyFlag.PHASED;
  else w.bodyFlags[i] &= ~BodyFlag.PHASED;
  // Issue #174: `t.passesLooseSolids` is static per archetype, resolved once
  // at spawn like every other `MovementTuning` field — this relay is what
  // `stepBody`'s own `solidIn` calls actually read, mirroring `PHASED` above.
  if (t.passesLooseSolids) w.bodyFlags[i] |= BodyFlag.BURROWS;
  else w.bodyFlags[i] &= ~BodyFlag.BURROWS;

  // The two forgiveness nudges come from tuning, not from module constants, so
  // docs/02's promise that every feel number is live-editable holds for them
  // too. Reusing one scratch object keeps the substep allocation-free.
  stepLimits.stepUp = t.ledgeSnap;
  stepLimits.cornerCorrection = t.cornerCorrection;
  // Issue #179: static per archetype, resolved once at spawn like
  // `passesLooseSolids` above — `stepBody`'s `squeezeBlocked` is what
  // actually reads it.
  stepLimits.minGapCells = t.minGapCells;
  const res = stepBody(w, i, cells, dt, stepLimits);
  handleContacts(m, w, i, res, ctx);

  // Dash exit preserves some momentum so flow survives the transition.
  if (m.dashTime <= 0 && m.state === MoveState.DASH) {
    w.vx[i] = m.dashDirX * t.dashSpeed * t.dashMomentumKeep;
    w.vy[i] = m.dashDirY * t.dashSpeed * t.dashMomentumKeep * 0.5;
  }

  updateSquash(m, dt);
  w.scaleX[i] = m.squashX;
  w.scaleY[i] = m.squashY;
  w.iframes[i] = Math.max(w.iframes[i], m.iframes);
}

function tickTimers(m: MoverState, dt: number): void {
  m.coyote = Math.max(0, m.coyote - dt);
  m.wallCoyote = Math.max(0, m.wallCoyote - dt);
  m.dashTime = Math.max(0, m.dashTime - dt);
  m.dashCooldown = Math.max(0, m.dashCooldown - dt);
  m.dashPostGravity = Math.max(0, m.dashPostGravity - dt);
  m.iframes = Math.max(0, m.iframes - dt);
  m.inputLock = Math.max(0, m.inputLock - dt);
  m.wallGrace = Math.max(0, m.wallGrace - dt);
  m.blinkCooldown = Math.max(0, m.blinkCooldown - dt);
  m.phaseTime = Math.max(0, m.phaseTime - dt);
  m.phaseCooldown = Math.max(0, m.phaseCooldown - dt);
  m.squashTime = Math.max(0, m.squashTime - dt);
  m.dropTime = Math.max(0, m.dropTime - dt);
  m.tailAnchorTime = Math.max(0, m.tailAnchorTime - dt);
  if (m.tailAnchorTime === 0) m.tailAnchored = false;

  // docs/02 §2.6: dash i-frames run 90 ms *starting at frame 2*. The activation
  // substep stays vulnerable on purpose — a dash is a commitment, not a panic
  // button you can throw out after the hit is already on you.
  if (m.dashTime > 0) {
    m.dashSubsteps++;
    if (m.dashSubsteps === 1) m.iframes = Math.max(m.iframes, m.tuning.dashIFrames);
  }
}

/**
 * The loadout's mobility cost this substep.
 *
 * docs/04 §8 promises the pair of weapons you carry changes how you *move*.
 * Combat drags velocity directly, but the controller re-accelerated to full run
 * speed within two substeps, so the drag was invisible and a Sunder Gauntlet
 * traversed a room exactly like a dagger. Scaling the *target* is what makes it
 * stick — and it is a drag, never a stop, which is the rule at the top of this
 * file.
 *
 * Read unconditionally rather than only while attacking, because `drag` now
 * carries a standing weight term as well as the per-swing one (issue #30): two
 * hammers were byte-identical to hammer-and-dagger, because only the hand that
 * happened to be swinging was consulted. The *pair* is the unit.
 */
function attackDrag(ctx: MoveContext): number {
  return ctx.combat?.drag ?? 1;
}

/**
 * Ground and air acceleration, with the turn bonus that does most of the work
 * of making the character feel responsive rather than merely fast.
 */
function applyHorizontal(
  m: MoverState,
  w: World,
  i: number,
  ix: number,
  grounded: boolean,
  submersion: number,
  dt: number,
  drag: number,
): void {
  const t = m.tuning;
  let vx = w.vx[i];

  let maxSpeed = t.maxRunSpeed * drag;
  // Berserker — issue #178. 0 for every other archetype, so this is a no-op
  // everywhere else: `1 + 0 * anything` is exactly 1.
  if (t.speedPerMissingHp !== 0 && w.maxHp[i] > 0) {
    const missingHpFraction = 1 - w.hp[i] / w.maxHp[i];
    maxSpeed *= 1 + t.speedPerMissingHp * clamp01(missingHpFraction);
  }
  let accel = (grounded ? t.groundAccel : t.airAccel) * drag;
  const decel = grounded ? t.groundDecel : t.airDecel;
  const turnBonus = grounded ? t.turnAccelBonus : t.airTurnBonus;

  if (submersion > 0.02) {
    const lm = w.submergedMat[i];
    const viscous = lm !== VOID ? M.viscosity[lm] : 0;
    maxSpeed *= lerp(1, t.fluidMaxSpeedScale * (1 - viscous * 0.6), submersion);
    accel *= lerp(1, t.fluidAccelScale, submersion);
  }

  // Surface material scales traction. Ice is genuinely slippery, and that is
  // the material system reaching into the movement package.
  if (grounded) {
    const gm = w.groundMat[i];
    if (gm !== VOID) {
      const f = M.friction[gm];
      accel *= clamp(f, 0.25, 1.2);
    }
  }

  // Issue #180. `slopeBias` is a live per-substep read against
  // `w.groundSlope[i]` (`body.ts`'s `groundSlopeAt`, sampled fresh every
  // `stepBody`), not a flat multiplier baked into `MovementTuning` at spawn —
  // the ground under the feet changes every step, so it cannot be resolved
  // once at archetype-select time, the same reasoning `speedPerMissingHp`'s
  // own comment gives for reading live HP instead of a static coefficient.
  // 0 for every archetype but Semi-liquid mutant, so `1 + 0 * anything` is
  // exactly 1 everywhere else — a no-op.
  if (grounded && t.slopeBias !== 0 && ix !== 0) {
    const slope = w.groundSlope[i];
    if (slope !== 0) {
      const grade = Math.min(1, Math.abs(slope));
      // +1 when the input points the same way the ground descends (downhill
      // — accelerate harder), -1 when it points the other way (uphill — the
      // same knob, in reverse, is the drag docs/02 §3.2 also promises).
      const downhillSign = Math.sign(ix) === Math.sign(slope) ? 1 : -1;
      accel *= Math.max(0.1, 1 + t.slopeBias * grade * downhillSign);
    }
  }

  if (ix !== 0) {
    const target = ix * maxSpeed;
    // Opposing the current velocity gets multiplied acceleration.
    const opposing = vx !== 0 && Math.sign(ix) !== Math.sign(vx);
    const a = accel * (opposing ? turnBonus : 1);
    // Preserve overspeed (from dashes, knockback, explosions) rather than
    // snapping back to run speed — momentum the player earned is theirs.
    if (Math.abs(vx) > maxSpeed && !opposing) {
      vx = approach(vx, target, decel * 0.25 * dt);
    } else {
      vx = approach(vx, target, a * dt);
    }
  } else {
    const gm = grounded ? w.groundMat[i] : VOID;
    const slick = gm !== VOID ? clamp(M.friction[gm], 0.1, 1.2) : 1;
    vx = approach(vx, 0, decel * slick * dt);
  }

  w.vx[i] = vx;
}

function applyVertical(
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  grounded: boolean,
  onWall: number,
  submersion: number,
  dt: number,
  ctx: MoveContext,
): void {
  const t = m.tuning;
  let vy = w.vy[i];

  if (m.state === MoveState.DASH) {
    w.vy[i] = vy;
    return;
  }

  if (m.state === MoveState.CLING) {
    // Cling: full vertical control, no gravity. Climbing is an input, not a slide.
    const iy = input ? input.axis.y : 0;
    vy = Math.abs(iy) > 0.2 ? iy * t.clingClimbSpeed : 0;
    w.vy[i] = vy;
    return;
  }

  // Issue #171. Full free vertical control — gravity cancelled entirely,
  // the same shape `CLING`'s climb gives, reusing `maxRunSpeed` rather than
  // inventing a second speed constant nothing has calibrated: "true flight"
  // reads as comparably mobile in both axes, not vertically slower than the
  // body already runs. Terrain collision is untouched — `stepBody`'s own
  // `solidIn` still applies in full; only gravity is what flight suspends
  // (the issue's own open question — answered by not touching `phased`).
  if (m.state === MoveState.FLIGHT) {
    const iy = input ? input.axis.y : 0;
    vy = Math.abs(iy) > 0.2 ? iy * t.maxRunSpeed * 0.6 : 0;
    w.vy[i] = vy;
    drainFlightFuel(m, dt);
    return;
  }

  // Issue #171. Thrust resists gravity heavily but does not grant full
  // control — the "hover" in hover-thrust: holding position, or climbing
  // slowly, is possible; a fast climb is not, so this stays a companion to
  // jump/wall-jump rather than a replacement for them the way full flight is.
  //
  // Eased toward the cap with `approach` rather than clamped there outright:
  // a raw clamp would spend most of a jump's own launch velocity in one
  // substep the instant thrust engages, which reads as hitting a ceiling,
  // not gravity being resisted. `approach` bleeds it off at the same rate
  // it fights an actual fall, so entering hover mid-jump and entering it
  // mid-fall both feel like the one mechanic.
  if (m.state === MoveState.HOVER) {
    const hoverCap = t.maxRunSpeed * 0.3;
    const rate = t.fallGravity * HOVER_GRAVITY_SCALE;
    vy = approach(vy, hoverCap, rate * dt);
    w.vy[i] = vy;
    drainFlightFuel(m, dt);
    return;
  }

  // Asymmetric gravity — the single most important jump-feel decision.
  //
  // Note what is *not* here: a heavier rise gravity while jump is released.
  // docs/02 §2.2 specifies one cut on early release, `releaseCutMultiplier`,
  // and that cut lives in `handleJump`. Charging near-double gravity on top of
  // it meant a released jump paid twice and a buffered tap-jump peaked around
  // 5 units instead of 54.
  let g: number;
  if (grounded) {
    g = t.fallGravity;
  } else if (Math.abs(vy) < t.apexThreshold) {
    g = t.apexGravity; // the hang, right where the player is aiming
  } else if (vy < 0) {
    g = t.riseGravity;
  } else {
    g = t.fallGravity;
  }

  if (m.dashPostGravity > 0) g *= t.dashPostGravityScale;
  g *= w.gravityScale[i];

  if (submersion > 0.02) {
    const lm = w.submergedMat[i];
    if (lm !== VOID) {
      // docs/02 §2.7: `gravity *= lerp(1, buoyancy(material), s)`. One model,
      // continuous in density — mercury throws you up, oil barely slows the
      // sink — replacing a binary dense/light branch here *and* a second
      // density impulse that body.ts used to add underneath it.
      const ratio = M.density[lm] / (1000 * w.mass[i]);
      const buoy = clamp(1 - ratio + t.fluidSinkBias, -1.2, 1);
      g *= lerp(1, buoy, submersion);
    }
  }

  vy += g * dt;

  // Terminal velocity, with fast-fall as an explicit input.
  const wantFast = input ? input.axis.y > 0.5 : false;
  const maxFall = wantFast ? t.fastFallSpeed : t.maxFallSpeed;
  if (vy > maxFall) vy = maxFall;

  if (m.state === MoveState.WALL_SLIDE && vy > 0) {
    if (m.wallGrace > 0) {
      vy = 0;
    } else {
      // Wall material sets the slide speed. Ice drops you; rough stone grips.
      //
      // The line is fixed by the two figures docs/02 §2.5 names, against a 118
      // base: ice (friction 0.12) reads 240, and the roughest wall in the game
      // — concrete, friction 0.98 — reads 62. The previous `1.6 - f` curve gave
      // 175 and 66; the ordering was right and the *feel*, which is the whole
      // point of quoting per-material numbers at all, was not.
      const wallMat = wallMaterial(ctx.cells, w, i, onWall);
      let slide = t.wallSlideSpeed;
      if (wallMat !== VOID) {
        const f = M.friction[wallMat];
        slide = t.wallSlideSpeed * clamp(2.244 - 1.754 * f, 0.5, 2.1);
      }
      vy = Math.min(vy, slide);
    }
  }

  if (m.state === MoveState.SWIM) {
    // The ceiling is the medium's, not the character's. A flat ±maxRunSpeed
    // matched Fluid Jet's own density-scaled cap only at water: in mercury the
    // jet reached 348 u/s sideways and this line deleted the identical upward
    // thrust down to 145 on the very next substep, so the ability docs/02 §3
    // row 7 builds out of the material table worked on one axis only.
    const lm = w.submergedMat[i];
    const s = lm !== VOID ? fluidSpeedScale(M.density[lm]) : 1;
    vy = clamp(vy, -t.maxRunSpeed * s, t.maxRunSpeed * 0.8 * s);
  }

  w.vy[i] = vy;
}

/**
 * "Anchor the tail and stretch": Segmented biomech's grounded jump, issue
 * #186. The card's own text is the spec — "reach replaces jump distance, and
 * it is aimable" — so this replaces `jumpVelocity(t)`'s straight-up launch
 * with one aimed at whatever 8-way direction the stick holds, at the same
 * total speed the vertical jump would have carried. Aim is read exactly the
 * way `handleDash` reads it a few lines below (world-space `ix`, raw
 * `input.axis.y`, an 8-way quantiser) rather than through the local-frame
 * `aimAngleFor` combat's aim uses, because this is a launch direction and not
 * a weapon angle — there is no `facing` to fold out of it.
 *
 * Neutral stick is not a special case in the code, and it should not read as
 * one: `dx = 0, dy = 0` falls through to the `dy = -1` default below, and the
 * resulting launch is `(0, -reachSpeed)` — `jumpVelocity(t)` bit for bit. A
 * body with this tuning off never reaches this function at all, so the
 * ordinary jump is provably unchanged either way.
 */
function tailReachLaunch(
  m: MoverState, w: World, i: number, input: Input, ix: number, t: MovementTuning,
): void {
  let dx = ix !== 0 ? Math.sign(ix) : 0;
  let dy = Math.abs(input.axis.y) > 0.5 ? Math.sign(input.axis.y) : 0;
  if (dx === 0 && dy === 0) dy = -1;
  const len = Math.hypot(dx, dy);
  const reachSpeed = -jumpVelocity(t);
  w.vx[i] = (dx / len) * reachSpeed;
  w.vy[i] = (dy / len) * reachSpeed;
  m.tailAnchored = true;
  m.tailAnchorTime = TAIL_ANCHOR_VISUAL_TIME;
}

function handleJump(
  m: MoverState,
  w: World,
  i: number,
  input: Input,
  ix: number,
  grounded: boolean,
  onWall: number,
  submersion: number,
  ctx: MoveContext,
): void {
  const t = m.tuning;
  const bufferMs = t.jumpBuffer * 1000;

  m.jumpHeld = input.held('jump');

  // Variable jump height: releasing early cuts the rise, once, by
  // `releaseCutMultiplier` (docs/02 §2.2).
  //
  // The cut is armed at launch only if the button was actually held then. A
  // buffered tap — press 110 ms before landing and release immediately, which
  // is how players really buffer and which docs/02 §5 requires to work — has
  // already been released by the time the jump fires. Cutting it there is not
  // an early release; it is punishing the player for buffering, and it turned
  // the most common jump in the game into a 9-unit hop.
  if (!m.jumpHeld && w.vy[i] < 0 && m.jumpCutArmed) {
    w.vy[i] *= t.releaseCutMultiplier;
    m.jumpCutArmed = false;
  }

  const wantJump = input.buffered('jump', bufferMs);
  if (!wantJump) return;

  // docs/02 §4: "Jump cancels on hit-confirm only." A swing is a commitment,
  // and without this the cancel window was unconditional — every whiff was
  // jump-cancellable, which is the commitment erased. The press is deliberately
  // *not* consumed: the buffer keeps filling through the swing, so the jump
  // fires the instant the attack ends or confirms.
  const c = ctx.combat;
  if (c !== undefined && c.attacking && !c.jumpCancel) return;

  const canGround = grounded || m.coyote > 0;
  const canWall = onWall !== 0 || m.wallCoyote > 0;
  const canSwim = submersion > 0.5;

  // ------------------------------------------------------------------------
  // The one place `down` is arbitrated (issue #87).
  //
  // `down` itself is never taken: aiming (#39), the grounded heavy entry and
  // every `onHeavy` chain branch, Burrow selection, fast-fall and the dash
  // quantiser all keep reading it. What is context-selected is *this button* —
  // the same shape #78 settled the pad's B on. A jump pressed while standing
  // on a one-way floor with down held is a drop; a jump pressed anywhere else,
  // or without down, or on rock, is a jump. `startDrop` refuses (returning
  // false) whenever the support is not purely semi-solid, so the press falls
  // through to the ordinary jump below rather than being eaten.
  // ------------------------------------------------------------------------
  if (grounded && wantsDrop(input.axis.y) && startDrop(m, w, i, ctx.cells)) {
    input.consume('jump', bufferMs);
    return;
  }

  if (canGround) {
    input.consume('jump', bufferMs);
    if (t.tailAnchorReach) {
      tailReachLaunch(m, w, i, input, ix, t);
    } else {
      w.vy[i] = jumpVelocity(t);
    }
    m.coyote = 0;
    m.jumpCutArmed = m.jumpHeld;
    m.squashX = 0.86;
    m.squashY = 1.18;
    m.squashTime = 0.09;
  } else if (canWall) {
    input.consume('jump', bufferMs);
    const dir = onWall !== 0 ? -onWall : -m.wallCoyoteDir;
    // Repeatedly jumping off the same wall pays a horizontal penalty, so
    // ascending a single wall is possible but not free.
    const penalty = m.lastWallDir === dir ? t.sameWallJumpPenalty : 1;
    w.vx[i] = dir * t.wallJumpImpulseX * penalty;
    w.vy[i] = t.wallJumpImpulseY;
    m.lastWallDir = dir;
    m.wallCoyote = 0;
    m.inputLock = t.wallJumpInputLock;
    m.jumpCutArmed = m.jumpHeld;
    m.facing = dir;
    if (ctx.onWallJump) ctx.onWallJump(dir);
  } else if (canSwim) {
    input.consume('jump', bufferMs);
    w.vy[i] = jumpVelocity(t) * t.fluidJumpScale;
    m.jumpCutArmed = m.jumpHeld;
  }
}

/**
 * Retire a live drop ticket once it has done its job.
 *
 * Run at the top of the substep, against the position `stepBody` left behind,
 * so the flag is only ever raised across integrations that need it.
 *
 * Four ways out, and the first is the one that matters:
 *
 *  1. **the feet passed the run's bottom** — the geometric exit, which is what
 *     makes this work on a 1-cell plank and on a 7-cell deck alike;
 *  2. **the body landed** on something the ticket does not make transparent,
 *     i.e. real matter, so holding it any longer buys nothing;
 *  3. **the body is rising** — a pogo, an explosion, an updraft. A rising body
 *     is not dropping through anything, and `body.ts` would not consult the
 *     ticket anyway;
 *  4. the timeout, which is only reachable if the body never moved.
 */
function updateDropThrough(m: MoverState, w: World, i: number): void {
  if (!m.dropping) return;
  const feet = w.y[i] + w.halfH[i];
  if (feet > m.dropExitY || w.grounded[i] === 1 || w.vy[i] < 0 || m.dropTime <= 0) {
    m.dropping = false;
    m.dropTime = 0;
    w.bodyFlags[i] &= ~BodyFlag.DROP_THROUGH;
  }
}

/**
 * Try to start a drop. Returns false — having changed nothing — when the floor
 * under the feet is not one the ticket can dissolve.
 *
 * The refusal is the interesting half. A body straddling a catwalk and a
 * concrete lip is standing on *rock* as far as `solidIn` is concerned, so
 * raising the ticket there spends the jump and moves nobody: the player presses
 * down+jump, hears nothing happen, and learns the control is unreliable. Two
 * conditions, both over the body's own footprint at the support row:
 * at least one one-way cell, and no solid one.
 */
function startDrop(m: MoverState, w: World, i: number, cells: CellWorld): boolean {
  const halfW = w.halfW[i];
  const feet = w.y[i] + w.halfH[i];
  // The same row `groundMaterialAt` calls the surface, so "what am I standing
  // on" has one definition and a body resting a fraction proud of the deck
  // still reads it.
  const row = Math.floor(feet + 0.5);
  const x0 = Math.floor(w.x[i] - halfW);
  const x1 = Math.floor(w.x[i] + halfW - 0.001);

  let sawOneWay = false;
  for (let x = x0; x <= x1; x++) {
    if (cells.isSolid(x, row)) return false;
    if (isOneWayCell(cells, x, row)) sawOneWay = true;
  }
  if (!sawOneWay) return false;

  // Descend to the bottom of the run. Per column, because a deck is not
  // guaranteed flat — take the deepest, or the shallow columns release the
  // ticket while the deep ones are still holding the body.
  //
  // The loop bound *is* the ticket's bound, and deliberately the only one: a
  // second `Math.min(..., feet + DROP_SCAN_MAX)` on the line below read as
  // prudence and could never bind, because `bottom` cannot leave this loop
  // further than `DROP_SCAN_MAX - 1` rows under `row`. A guard no test can
  // kill is the defect this repo keeps finding, so there is one bound here.
  let bottom = row;
  for (let d = 1; d < DROP_SCAN_MAX; d++) {
    const r = row + d;
    let any = false;
    for (let x = x0; x <= x1 && !any; x++) any = isOneWayCell(cells, x, r);
    if (!any) break;
    bottom = r;
  }

  m.dropping = true;
  // `+1` is the first row at which the run's last cell can no longer catch the
  // feet: `solidIn` blocks a one-way cell at row `r` while the pre-move feet
  // are at or above `r`, so the feet have to finish strictly below it.
  m.dropExitY = bottom + 1;
  m.dropTime = DROP_TIMEOUT;
  w.bodyFlags[i] |= BodyFlag.DROP_THROUGH;
  return true;
}

// ---------------------------------------------------------------- crouch

/**
 * Take the stance, or give it up — issue #98.
 *
 * THE COLLIDER MOVE, and why it is a subtraction rather than a scale of `y`.
 * The feet are the anchor: `halfH` comes down by `drop` and the centre goes
 * down by the same amount, so `y + halfH` — which is every ground probe in
 * `body.ts`, `groundMaterialAt`, and the wound mapping in
 * `pixelBodySystem.ts` — is unchanged across the transition. Shrinking about
 * the centre instead lifts the body half a crouch off the floor, it falls back
 * the next substep, and ducking reads as a stumble.
 *
 * THE STAND-UP GUARD, which is the whole of the rest of this function. It is
 * not a corner case: over the real generator, 9.31% of floor cells have
 * headroom for a crouch and none for standing, there are 2 198 crouch-only runs
 * three or more cells wide, and 52% of those adjoin floor you can stand on
 * (#98). So "walk in upright, crouch, move in, release" happens constantly, and
 * the two ways to get it wrong are both shipped bugs elsewhere: stand anyway
 * and the body is inside rock, or refuse forever and the player is welded to
 * the floor. The rule here is the third thing — **the crouch outlives the
 * button.** Releasing `down` is a request, `canStand` is the answer, and the
 * request is re-asked every substep so walking back out stands you up with no
 * second press. `standBlocked` records which of the two is happening.
 *
 * The probe is `solidIn` over the *whole* standing box, not a scan of the rows
 * above the head. Same predicate the collider will use one line later, so the
 * two cannot disagree about a straddled cell or about a one-way platform
 * overhead — a semi-solid ceiling is air to a body standing up into it, and a
 * bespoke head scan is exactly where that gets forgotten.
 */
function updateCrouch(
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  cells: CellWorld,
  grounded: boolean,
  staggered: boolean,
): void {
  // A crouch is a stance, so it needs something to stand on. Airborne — walked
  // off a ledge, dropped through a plank, launched — the body stands back up
  // as soon as there is room for it, and stays folded when there is not.
  //
  // Stagger takes the stance for the same reason it takes the stick: input
  // authority is suspended, and a held button must not keep acting.
  const want = input !== null && grounded && !staggered && wantsCrouch(input.axis.y);

  if (!m.crouching) {
    m.standBlocked = false;
    if (!want) return;
    const stand = w.halfH[i];
    const drop = stand * (1 - CROUCH_HEIGHT);
    if (drop <= 0) return;
    w.halfH[i] = stand - drop;
    w.crouchDrop[i] = drop;
    // Feet stay put: the centre falls by exactly what the box lost.
    w.y[i] += drop;
    m.crouching = true;
    return;
  }

  if (want) {
    m.standBlocked = false;
    return;
  }

  const drop = w.crouchDrop[i];
  const stand = w.halfH[i] + drop;
  const feet = w.y[i] + w.halfH[i];
  const phased = (w.bodyFlags[i] & BodyFlag.PHASED) !== 0;
  if (solidIn(cells, w.x[i], feet - stand, w.halfW[i], stand, phased, ONE_WAY_IGNORE, m.tuning.passesLooseSolids)) {
    m.standBlocked = true;
    return;
  }
  w.halfH[i] = stand;
  w.crouchDrop[i] = 0;
  w.y[i] = feet - stand;
  m.crouching = false;
  m.standBlocked = false;
}

/** A semi-solid cell that is not currently phased out by the world. */
function isOneWayCell(cells: CellWorld, x: number, y: number): boolean {
  const mat = cells.peek(x, y);
  if (mat === VOID || M.oneWay[mat] !== OneWay.UP) return false;
  return (cells.getFlags(x, y) & Flag.PHASED) === 0;
}

/**
 * The recorded spot closest to `targetAge` seconds old that is not currently
 * solid — issue #177. The world is destructible, so the spot the mover left
 * may have collapsed (or been filled) behind it since; this walks forward
 * through the history toward *now* until it finds one that is still open,
 * the same precondition Blink's snap-back checks against its own origin
 * (`abilities.ts`'s `findFreeBox` call on `m.snapbackX/Y`). Returns `null`
 * only if every recorded spot, including the current one, reads solid.
 */
function rewindTarget(
  hist: { x: number; y: number; age: number }[],
  targetAge: number,
  cells: CellWorld,
  hw: number,
  hh: number,
): { x: number; y: number } | null {
  if (hist.length === 0) return null;
  let start = hist.length - 1; // not enough history yet: fall back to newest
  for (let k = 0; k < hist.length; k++) {
    if (hist[k].age <= targetAge) { start = k; break; }
  }
  for (let k = start; k < hist.length; k++) {
    const h = hist[k];
    if (!solidIn(cells, h.x, h.y, hw, hh, false)) return { x: h.x, y: h.y };
  }
  return null;
}

function handleDash(
  m: MoverState,
  w: World,
  i: number,
  input: Input,
  ix: number,
  ctx: MoveContext,
): void {
  const t = m.tuning;
  if (m.dashCooldown > 0 || m.dashTime > 0) return;

  const grounded = w.grounded[i] === 1;
  const hasCharge = m.dashesLeft > 0 || (!grounded && m.airDashesLeft > 0);
  if (!hasCharge) return;

  if (t.dashMode === 'rewind') {
    const target = rewindTarget(m.rewindHistory, t.dashRewindSeconds, ctx.cells, w.halfW[i], w.halfH[i]);
    if (target === null) return; // no safe recorded spot yet — refuse rather than teleport blind
    if (!input.consume('dash', 120)) return;
    w.x[i] = target.x;
    w.y[i] = target.y;
    w.vx[i] = 0;
    w.vy[i] = 0;
    // Zeroed rather than aimed: the forced-velocity line below (and the exit
    // momentum it feeds after `dashTime` runs out) becomes a no-op, which is
    // the whole difference from `'translate'` — "escapes, never advances."
    m.dashDirX = 0;
    m.dashDirY = 0;
    m.dashTime = t.dashDuration;
    m.dashCooldown = t.dashDuration + t.dashCooldown;
    m.dashPostGravity = t.dashDuration + t.dashPostGravityTime;
    m.dashSubsteps = 0;
    if (grounded || m.dashesLeft > 0) m.dashesLeft--;
    else m.airDashesLeft--;
    if (ctx.onDash) ctx.onDash(0, 0);
    return;
  }

  if (!input.consume('dash', 120)) return;

  // Aim: 8-way from the stick, defaulting to facing when neutral.
  let dx = ix !== 0 ? Math.sign(ix) : 0;
  let dy = Math.abs(input.axis.y) > 0.5 ? Math.sign(input.axis.y) : 0;
  if (dx === 0 && dy === 0) dx = m.facing;
  const len = Math.hypot(dx, dy) || 1;
  m.dashDirX = dx / len;
  m.dashDirY = dy / len;

  m.dashTime = t.dashDuration;
  m.dashCooldown = t.dashDuration + t.dashCooldown;
  m.dashPostGravity = t.dashDuration + t.dashPostGravityTime;
  // i-frames are armed by `tickTimers` on the next substep, not here — the
  // window starts at frame 2 (docs/02 §2.6).
  m.dashSubsteps = 0;
  if (grounded || m.dashesLeft > 0) m.dashesLeft--;
  else m.airDashesLeft--;
  if (dx !== 0) m.facing = Math.sign(dx);

  if (ctx.onDash) ctx.onDash(m.dashDirX, m.dashDirY);
}

/**
 * The hinge between combat and traversal: confirming a hit refreshes dash and
 * Airstep, so aggression is mobility and a skilled player crosses a room
 * without touching the floor.
 */
export function refreshOnHitConfirm(m: MoverState): void {
  m.dashesLeft = Math.max(m.dashesLeft, 1);
  if ((m.abilities & Ability.AIRSTEP) !== 0) {
    m.airDashesLeft = Math.max(m.airDashesLeft, 1);
  }
  m.dashCooldown = Math.min(m.dashCooldown, 0.05);
}

/** Down-air rebound. Legal off any solid, and off dense liquid surfaces. */
export function pogo(m: MoverState, w: World): void {
  const i = m.entity;
  w.vy[i] = -m.tuning.pogoImpulse;
  m.dashesLeft = Math.max(m.dashesLeft, 1);
  if ((m.abilities & Ability.AIRSTEP) !== 0) m.airDashesLeft = Math.max(m.airDashesLeft, 1);
  m.squashX = 1.15;
  m.squashY = 0.85;
  m.squashTime = 0.1;
}

function handleContacts(
  m: MoverState,
  w: World,
  i: number,
  res: BodyResult,
  ctx: MoveContext,
): void {
  const t = m.tuning;

  if (res.landed) {
    const impact = Math.abs(res.landingSpeed);
    m.landImpact = impact;

    // `w.groundMat` samples half a cell below the feet, which is right for a
    // settled body and wrong for the substep a fast one lands on: the swept
    // step stops at the last free position, up to a full sub-step short of the
    // surface at 620 u/s, so the probe read air. Every consequence of a heavy
    // landing — the debris, the crack, the material passed to `onLand` — hung
    // off that read and therefore never fired on the landings that matter.
    let gm = VOID;
    let gRow = Math.floor(w.y[i] + w.halfH[i] + 0.5);
    for (let d = 0; d <= 3 && gm === VOID; d++) {
      gm = groundMaterialAt(ctx.cells, w.x[i], w.y[i] + d, w.halfW[i], w.halfH[i]);
      gRow = Math.floor(w.y[i] + d + w.halfH[i] + 0.5);
    }

    if (impact > t.landSoftThreshold) {
      const hard = impact > t.landHardThreshold;
      m.squashX = hard ? 1.24 : 1.14;
      m.squashY = hard ? 0.78 : 0.88;
      m.squashTime = hard ? 0.15 : 0.1;

      // A heavy landing throws real cells. This detail sells "the world is
      // matter" harder than any set piece.
      if (ctx.particles) {
        const count = hard ? 6 : 2;
        if (gm !== VOID && M.category[gm] === Category.LOOSE) {
          for (let k = 0; k < count; k++) {
            const px = Math.floor(w.x[i] + (k - count / 2) * 2);
            ctx.particles.promote(px, gRow, (k - count / 2) * 26, -impact * 0.14);
          }
        }
      }

      // Hard landings crack brittle floor — a movement verb with a terrain
      // consequence, which is the shape every ability should aspire to.
      if (hard) {
        // 40 ms of hitstop (docs/02 §2.4), the same freeze a heavy weapon hit
        // gets. It is feedback, not a control penalty: buffers keep filling
        // through it (§4), so the jump you entered on impact still fires.
        w.hitstop[i] = Math.max(w.hitstop[i], t.landHitstop);
        if (gm !== VOID && M.brittleness[gm] > 0.6) {
          ctx.cells.damageCircle(w.x[i], gRow, 3, M.hardness[gm]);
        }
      }
    } else {
      m.squashX = 1.06;
      m.squashY = 0.95;
      m.squashTime = 0.06;
    }

    if (ctx.onLand) ctx.onLand(impact, w.x[i], w.y[i], gm);
    // Critically: no input lockout. Not one frame.
  }

  // Grace arms on the *transition* into wall contact, not on every substep we
  // happen to be touching a wall. Re-arming continuously (the obvious-looking
  // `hitX && grace <= 0` form) means the grace timer never expires while you
  // hold into the wall, and the player sticks there permanently instead of
  // sliding.
  const onWall = w.onWall[i];
  if (onWall !== 0 && m.prevOnWall === 0) {
    m.wallGrace = t.wallSlideGrace;
  }
  m.prevOnWall = onWall;
}

/**
 * Air-dash budget. Airstep *is* the budget's source (docs/02 §6 rule 1: a
 * pickup that grants nothing in an empty room is a key, not a mechanic); an
 * archetype's `maxAirDashes` stacks on top of it rather than substituting for
 * it. That is also what makes the Duelist's advertised "two air dashes once
 * Airstep is found" literally true, instead of one air dash from minute zero
 * and no change when the ability drops.
 */
function airDashBudget(m: MoverState): number {
  if ((m.abilities & Ability.AIRSTEP) === 0) return 0;
  return 1 + m.tuning.maxAirDashes;
}

/** Is the player asking to grip, and do they own the ability to do it? */
function wantsGrip(m: MoverState, input: Input | null): boolean {
  if ((m.abilities & Ability.CLING) === 0) return false;
  // AI movers have no grip button and never cling; they wall-slide.
  return input !== null && input.held('grip');
}

/**
 * Cling's material precondition — ice, oiled, and corroding surfaces refuse the
 * grip (docs/02 §3). This is what keeps the ability a mechanic rather than a
 * key, and it applies only to the *grip*: an ungripped Cling owner still
 * wall-slides down ice at ice's slide speed.
 */
function canCling(
  m: MoverState,
  cells: CellWorld,
  w: World,
  i: number,
  dir: number,
): boolean {
  const mat = wallMaterial(cells, w, i, dir);
  if (mat === VOID) return false;
  if ((M.tags[mat] & Tag.SLICK) !== 0) return false;

  const wx = Math.floor(w.x[i] + dir * (w.halfW[i] + 1));
  const wy = Math.floor(w.y[i]);
  if ((cells.getFlags(wx, wy) & Flag.CORRODING) !== 0) return false;

  // "Oiled" is not a material, it is a film: a slick liquid running down the
  // contact column of an otherwise grippable wall. Sample the cell the body's
  // own edge occupies, so a stone shaft someone has poured oil down refuses the
  // grip exactly like ice does — docs/02 §2.5, oiled walls are a trap. The
  // friction test is what separates oiled from merely wet: water is SLICK too,
  // and a rained-on wall is not an oiled one.
  const film = cells.peek(Math.floor(w.x[i] + dir * w.halfW[i]), wy);
  if (film !== VOID && (M.tags[film] & Tag.SLICK) !== 0 && M.friction[film] < 0.2) {
    return false;
  }
  return true;
}

function wallMaterial(cells: CellWorld, w: World, i: number, dir: number): number {
  if (dir === 0) return VOID;
  const x = Math.floor(w.x[i] + dir * (w.halfW[i] + 1));
  const y = Math.floor(w.y[i]);
  return cells.peek(x, y);
}

function updateSquash(m: MoverState, dt: number): void {
  if (m.squashTime > 0) return;
  // Settle back to 1 quickly but not instantly.
  m.squashX = approach(m.squashX, 1, 6 * dt);
  m.squashY = approach(m.squashY, 1, 6 * dt);
}
